import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { debtsApi } from '@/lib/api'
import { Debt } from '@/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const debtSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  debt_type: z.enum(['credit_card', 'student_loan', 'auto_loan', 'personal_loan', 'mortgage', 'other']),
  current_balance: z.number().positive('Balance must be positive'),
  interest_rate: z.number().min(0).max(100, 'Rate must be 0–100'),
  minimum_payment: z.number().positive('Minimum payment must be positive'),
  notes: z.string().optional(),
})

type DebtFormData = z.infer<typeof debtSchema>

const DEBT_TYPE_LABELS: Record<string, string> = {
  credit_card: 'Credit Card',
  student_loan: 'Student Loan',
  auto_loan: 'Auto Loan',
  personal_loan: 'Personal Loan',
  mortgage: 'Mortgage',
  other: 'Other',
}

interface DebtFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  debt?: Debt
  onSuccess: () => void
}

export default function DebtFormDialog({ open, onOpenChange, debt, onSuccess }: DebtFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DebtFormData>({
    resolver: zodResolver(debtSchema),
    defaultValues: debt
      ? {
          name: debt.name,
          debt_type: debt.debt_type,
          current_balance: debt.current_balance,
          interest_rate: debt.interest_rate,
          minimum_payment: debt.minimum_payment,
          notes: debt.notes ?? '',
        }
      : {
          name: '',
          debt_type: 'credit_card',
          current_balance: undefined,
          interest_rate: undefined,
          minimum_payment: undefined,
          notes: '',
        },
  })

  const debtTypeValue = watch('debt_type')

  // Re-populate form when `debt` changes (e.g. opening edit for a different debt)
  useEffect(() => {
    if (debt) {
      reset({
        name: debt.name,
        debt_type: debt.debt_type,
        current_balance: debt.current_balance,
        interest_rate: debt.interest_rate,
        minimum_payment: debt.minimum_payment,
        notes: debt.notes ?? '',
      })
    } else {
      reset({
        name: '',
        debt_type: 'credit_card',
        current_balance: undefined,
        interest_rate: undefined,
        minimum_payment: undefined,
        notes: '',
      })
    }
  }, [debt, reset])

  const mutation = useMutation({
    mutationFn: (data: DebtFormData) => {
      if (debt) {
        return debtsApi.update(debt.id, data)
      }
      return debtsApi.create(data)
    },
    onSuccess: () => {
      onSuccess()
      onOpenChange(false)
    },
  })

  function onSubmit(data: DebtFormData) {
    mutation.mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{debt ? 'Edit Debt' : 'Add Debt'}</DialogTitle>
          <DialogDescription>
            {debt ? 'Update your debt details.' : 'Add a new debt to track.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="debt-name">Name</Label>
            <Input
              id="debt-name"
              placeholder="e.g. Chase Sapphire Card"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Debt Type */}
          <div className="space-y-1.5">
            <Label>Debt Type</Label>
            <Select
              value={debtTypeValue}
              onValueChange={(v) => setValue('debt_type', v as DebtFormData['debt_type'])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DEBT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.debt_type && (
              <p className="text-xs text-destructive">{errors.debt_type.message}</p>
            )}
          </div>

          {/* Current Balance */}
          <div className="space-y-1.5">
            <Label htmlFor="debt-balance">Current Balance</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                id="debt-balance"
                type="number"
                min={0}
                step="0.01"
                className="pl-7"
                placeholder="0.00"
                {...register('current_balance', { valueAsNumber: true })}
              />
            </div>
            {errors.current_balance && (
              <p className="text-xs text-destructive">{errors.current_balance.message}</p>
            )}
          </div>

          {/* Interest Rate */}
          <div className="space-y-1.5">
            <Label htmlFor="debt-rate">Interest Rate</Label>
            <div className="relative">
              <Input
                id="debt-rate"
                type="number"
                min={0}
                max={100}
                step="0.01"
                className="pr-8"
                placeholder="0.00"
                {...register('interest_rate', { valueAsNumber: true })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
            </div>
            {errors.interest_rate && (
              <p className="text-xs text-destructive">{errors.interest_rate.message}</p>
            )}
          </div>

          {/* Minimum Monthly Payment */}
          <div className="space-y-1.5">
            <Label htmlFor="debt-min-payment">Minimum Monthly Payment</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                id="debt-min-payment"
                type="number"
                min={0}
                step="0.01"
                className="pl-7"
                placeholder="0.00"
                {...register('minimum_payment', { valueAsNumber: true })}
              />
            </div>
            {errors.minimum_payment && (
              <p className="text-xs text-destructive">{errors.minimum_payment.message}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="debt-notes">Notes (optional)</Label>
            <Textarea
              id="debt-notes"
              placeholder="Any additional notes..."
              rows={2}
              {...register('notes')}
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-destructive">
              Failed to save debt. Please try again.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : debt ? 'Save Changes' : 'Add Debt'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
